import { AnimatePresence, motion } from 'motion/react';
import { PageMotion } from 'src/components/layout/page-motion.tsx';
import { SECTIONS, TOTAL_QUESTIONS, sectionQuestions } from 'src/data/sections.ts';
import { useCheckFlow } from 'src/hooks/use-check-flow.ts';
import { QUESTION_TRANSITION } from 'src/lib/motion.ts';
import { subgroupLabel } from 'src/lib/scoring.ts';

import { BackButton } from '../components/ui/back-button.tsx';
import { LikertScale } from '../components/ui/likert-scale.tsx';
import { SectionTransition } from '../components/ui/section-transition.tsx';
import { SubgroupHeader } from '../components/ui/subgroup-header.tsx';

export function CheckPage() {
  const flow = useCheckFlow();

  if (flow.phase === 'transition') {
    return (
      <SectionTransition fromIdx={flow.fromIdx} onContinue={flow.onContinue} onBack={flow.onBack} />
    );
  }

  const {
    answers,
    sectionIdx,
    subgroupIdx,
    sec,
    sub,
    question,
    current,
    onAnswer,
    onBack,
    isFirstQuestion,
    legendOverride,
    setLegendOverride
  } = flow;
  const Icon = sec.icon;
  // Sticky User-Wahl gewinnt; sonst Default (offen nur auf der allerersten Frage).
  const legendOpen = legendOverride ?? isFirstQuestion;
  const answeredCount = Object.keys(answers).length;
  const overallPct = (answeredCount / TOTAL_QUESTIONS) * 100;

  return (
    <PageMotion>
      <div className="relative min-h-[calc(100vh-4rem)] flex flex-col">
        <div className="sticky top-0 z-30 border-b border-zeb-muted bg-white/95 backdrop-blur">
          <div className="mx-auto max-w-3xl px-5 lg:px-10 pt-5 pb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon className="h-4 w-4 text-zeb-primary shrink-0" />
                <div className="text-sm text-zeb-gray-600 truncate">
                  <span className="text-zeb-tertiary">{sec.title}</span>
                  {sub.title && <span className="text-zeb-gray-500"> · {sub.title}</span>}
                </div>
              </div>
              <div className="text-xs tabular-nums text-zeb-gray-500 shrink-0">{sec.kicker}</div>
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(overallPct)}
              aria-label="Fortschritt im Check"
              className="mt-3 flex gap-1.5">
              {SECTIONS.map((s, i) => {
                const qs = sectionQuestions(s);
                const sectionAnswered = qs.filter((q) => q.id in answers).length;
                const sectionPct = (sectionAnswered / qs.length) * 100;
                return (
                  <div key={s.id} className="flex-1 h-1 rounded-full bg-zeb-muted overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        i === sectionIdx
                          ? 'bg-zeb-primary'
                          : sectionPct === 100
                            ? 'bg-zeb-secondary'
                            : 'bg-zeb-primary/60'
                      }`}
                      style={{ width: `${sectionPct}%` }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-3xl px-5 lg:px-10 py-10 lg:py-16 flex-1">
          {sub.title && (
            <SubgroupHeader
              title={sub.title}
              count={sub.questions.length}
              positionLabel={subgroupLabel(sec, subgroupIdx)}
            />
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={QUESTION_TRANSITION}>
              <h2
                className="mt-5 text-title-sm sm:text-title-lg lg:text-heading-sm leading-[1.3] tracking-tight text-zeb-tertiary"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                {question.text}
              </h2>

              <LikertScale
                value={current}
                onPick={onAnswer}
                legendOpen={legendOpen}
                onToggleLegend={() => setLegendOverride(!legendOpen)}
              />
            </motion.div>
          </AnimatePresence>

          <div className="mt-12">
            <BackButton onClick={onBack} />
          </div>
        </div>
      </div>
    </PageMotion>
  );
}
